import Web3 from 'web3';

/**
 * Tests multiple BSC RPC endpoints and returns two Web3 instances using the fastest and second fastest ones
 * @param {Object} logger - Optional logger object with addLog method
 * @returns {Promise<{fastest: Web3, second: Web3}>} Object containing the fastest and second fastest Web3 instances
 */
export const getFastestWeb3 = async (logger = null) => {
  // List of BSC RPC endpoints to test
  const endpointsxxx = [
    'https://bsc-dataseed.nariox.org',
    'https://bsc-dataseed.defibit.io',
    'https://bsc-dataseed.ninicoin.io',
    'https://bsc.nodereal.io',
    'https://bsc-dataseed-public.bnbchain.org',
    "https://bsc-rpc.publicnode.com",
    "https://rpc-bsc.48.club",
  ];
  // uniq to endpoints
  const endpoints = [...new Set(endpointsxxx)];
  // Function to test a single RPC endpoint
  const testEndpoint = async (endpoint) => {
    const startTime = performance.now();
    const web3 = new Web3(endpoint);
    
    try {
      // Create a timeout promise that rejects after 4 seconds
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Timeout after 4 seconds')), 5000);
      });
      
      // Race between the actual request and the timeout
      const blockNumber = await Promise.race([
        web3.eth.getBlockNumber(),
        timeoutPromise
      ]);
      const latency = performance.now() - startTime;
      
      return {
        endpoint,
        blockNumber: BigInt(blockNumber),
        latency,
        web3
      };
    } catch (error) {
    //   console.error(`Error testing endpoint ${endpoint}:`, error);
      return {
        endpoint,
        blockNumber: BigInt(0),
        latency: Infinity,
        web3: null
      };
    }
  };

  try {
  
    // Test all endpoints concurrently
    const results = await Promise.all(endpoints.map(testEndpoint));
    
    // Filter out failed endpoints
    const validResults = results.filter(result => result.web3 !== null);
    
    if (validResults.length === 0) {
    //   console.error('All endpoints failed to respond');
      // Fallback to the first endpoint if all tests fail
      const fallbackWeb3 = new Web3(endpoints[0]);
      return {
        fastest: fallbackWeb3,
        second: new Web3(endpoints[1] || endpoints[0])
      };
    }
    
    // Find the highest block number
    const maxBlockNumber = validResults.reduce(
      (max, result) => result.blockNumber > max ? result.blockNumber : max,
      BigInt(0)
    );
    
    // Filter endpoints with the highest block number
    const mostUpToDateEndpoints = validResults.filter(
      result => result.blockNumber === maxBlockNumber
    );
    
    // Sort by latency (lowest first)
    mostUpToDateEndpoints.sort((a, b) => a.latency - b.latency);
    
    // Get the fastest and second fastest endpoints
    const fastest = mostUpToDateEndpoints[0];
    const second = mostUpToDateEndpoints[1] || mostUpToDateEndpoints[0]; // fallback to fastest if only one available
    
    const logMessage = `选择最快的两个https节点: 
第一: ${fastest.endpoint} (Block: ${fastest.blockNumber.toString()}, Latency: ${fastest.latency.toFixed(2)}ms)
第二: ${second.endpoint} (Block: ${second.blockNumber.toString()}, Latency: ${second.latency.toFixed(2)}ms)`;
    
    if (logger) {
      logger.addLog(logMessage);
    } else {
      console.log(logMessage);
    }
    
    return {
      fastest: fastest.web3,
      second: second.web3
    };
  } catch (error) {
    // console.error('Error finding fastest endpoint:', error);
    // Fallback to the first two endpoints if there's an error
    return {
      fastest: new Web3('https://bsc-dataseed.bnbchain.org'),
      second: new Web3('https://bsc-dataseed.nariox.org')
    };
  }
};

export default getFastestWeb3;